import axios from 'axios';

interface LeaderboardEntry {
  user_email: string;
  nickname: string;
  total_score: number;
  games_played: number;
  high_score: number;
  average_score: number;
}

interface GameTypeStats {
  games_played: number;
  total_score: number;
  high_score: number;
}

interface UserStats {
  total_score: number;
  games_played: number;
  high_score: number;
  average_score: number;
  by_game_type: {
    [key: string]: GameTypeStats;
  };
}

interface UserGame {
  id: string;
  game_type: string;
  difficulty: string;
  status: string;
  reaction: string;
  current_prompt: string;
  score: number;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  history?: Array<{
    action: string;
    response: {
      status: string;
      reaction: string;
      feedback: string;
      next_prompt: string;
      score_adjustment: number;
      suggestions: string[];
      response_quality: string;
    };
    timestamp: string;
    score_change: number;
  }>;
}

interface GameStartResponse {
  session_id: string;
  game_type: string;
  difficulty: string;
  status: string;
  reaction: string;
  scenario: string;
  score: number;
  suggestions: string[];
}

interface GameActionResponse {
  status: string;
  reaction: string;
  feedback: string;
  next_prompt: string;
  score_adjustment: number;
  suggestions: string[];
  response_quality: string;
}

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';

export const startGame = async (gameType: string, difficulty: string): Promise<GameStartResponse> => {
  const token = localStorage.getItem('token');
  const url = `${API_URL}/game/start`;
  
  try {
    const response = await axios.post(url, 
      {
        game_type: gameType,
        difficulty: difficulty,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error starting game:', error);
    throw error;
  }
};

export const performGameAction = async (sessionId: string, action: string): Promise<GameActionResponse> => {
  const token = localStorage.getItem('token');
  const url = `${API_URL}/game/action`;
  
  try {
    const response = await axios.post(url,
      {
        session_id: sessionId,
        action: action,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error performing game action:', error);
    throw error;
  }
};

export const getLeaderboard = async (gameType?: string): Promise<LeaderboardEntry[]> => {
  const token = localStorage.getItem('token');
  const url = `${API_URL}/game/leaderboard${gameType ? `?game_type=${gameType}` : ''}`;
  
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching leaderboard:', error);
    throw error;
  }
};

export const getUserStats = async (): Promise<UserStats> => {
  const token = localStorage.getItem('token');
  const url = `${API_URL}/game/my-stats`;
  
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
};

export const getUserGames = async (): Promise<UserGame[]> => {
  const token = localStorage.getItem('token');
  const url = `${API_URL}/game/my-games`;
  
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching user games:', error);
    if (axios.isAxiosError(error)) {
      console.error('Response:', error.response?.data);
      console.error('Status:', error.response?.status);
    }
    throw error;
  }
};

export const getGameById = async (gameId: string): Promise<UserGame> => {
  const token = localStorage.getItem('token');
  const url = `${API_URL}/game/${gameId}`;
  
  try {
    const response = await axios.get(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching game details:', error);
    throw error;
  }
}; 